<template>
    <div>
        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-body">
                        <div class="row mb-3">
                            <div class="col-12">
                                <div class="row">
                                    <div class="col-md-3">
                                        <b-form-input
                                            v-model="filter"
                                            class="form-control"
                                            type="search"
                                            :placeholder="$t('label.search')"
                                            v-on:change="searchListing"
                                            autocomplete="off"
                                        />
                                    </div>
                                    <div class="col-md-6"></div>
                                    <div class="col-md-3 d-flex justify-content-end align-items-center">
                                        <b-dropdown
                                            variant="warning"
                                            no-caret
                                            menu-class="dropdown-menu-250"
                                            class="mr-2"
                                            right
                                            v-b-tooltip.hover
                                            :title="$t('label.filter')"
                                        >
                                            <template #button-content>
                                                <i class="mdi mdi-filter"></i>
                                            </template>
                                            <div class="px-3 pb-2">
                                                <label class="small text-muted mb-1">
                                                    {{ $t('module.group') }}
                                                </label>
                                                <multiselect
                                                    v-model="groupFilter"
                                                    track-by="value"
                                                    label="text"
                                                    :placeholder="'Select'"
                                                    :select-label="'Select'"
                                                    :selected-label="'Selected'"
                                                    :deselect-label="'Select'"
                                                    :options="groupDdl"
                                                    :searchable="true"
                                                    :allow-empty="false"
                                                    @input="searchListing"
                                                />
                                            </div>
                                            <div class="px-3 pb-2">
                                                <label class="small text-muted mb-1">
                                                    {{ $t('label.type') }}
                                                </label>
                                                <multiselect
                                                    v-model="propertyTypeFilter"
                                                    track-by="value"
                                                    label="text"
                                                    :placeholder="'Select'"
                                                    :select-label="'Select'"
                                                    :selected-label="'Selected'"
                                                    :deselect-label="'Select'"
                                                    :options="propertyTypeDdl"
                                                    :searchable="true"
                                                    :allow-empty="false"
                                                    @input="searchListing"
                                                />
                                            </div>
                                            <div class="px-3 pb-2">
                                                <label class="small text-muted mb-1">
                                                    {{ $t('module.status') }}
                                                </label>
                                                <multiselect
                                                    v-model="isActive"
                                                    track-by="value"
                                                    label="text"
                                                    :placeholder="'Select'"
                                                    :select-label="'Select'"
                                                    :selected-label="'Selected'"
                                                    :deselect-label="'Select'"
                                                    :options="statusDdl"
                                                    :searchable="true"
                                                    :allow-empty="false"
                                                    @input="searchListing"
                                                />
                                            </div>
                                        </b-dropdown>
                                        <b-button
                                            variant="info"
                                            v-on:click="openForm"
                                            v-b-tooltip.hover
                                            :title="$t('label.createX', [])"
                                        >
                                            <i class="mdi mdi-plus"></i>
                                        </b-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="table-responsive mb-0">
                            <b-table
                                v-if="status.list && !loading"
                                :items="tableRows"
                                :fields="fields"
                                :per-page="perPage"
                                responsive="sm"
                            >
                                <template #cell(landlord)="data">
                                    <span>{{ data.item.tenanted?.landlord ?? '--' }}</span>
                                </template>
                                <template #cell(company)="data">
                                    <div class="d-flex align-items-center">
                                        <div class="avatar-xs mr-2">
                                            <span
                                                v-if="!data.item.imageUrl"
                                                :class="[
                                                    'avatar-title rounded-circle font-size-14',
                                                    `bg-soft-${avatarColor(data.item.company)}`,
                                                    `text-${avatarColor(data.item.company)}`
                                                ]"
                                            >
                                                {{ data.item.company ? data.item.company.charAt(0).toUpperCase() : '-' }}
                                            </span>
                                            <img
                                                v-else
                                                :src="data.item.imageUrl"
                                                v-on:click.stop="openImageModal(data.item)"
                                                class="mr-2 cursor-pointer"
                                                style="width: 30px; height: 30px; border-radius: 50%; object-fit: cover;"
                                            />
                                        </div>
                                        <CompanyLink :company="data.item.company" :uuid="data.item.companyUUID" fallback="--" />
                                    </div>
                                </template>
                                <template #cell(registeredOwner)="data">
                                    <span>{{ data.item.registeredOwner || '--' }}</span>
                                </template>
                                <template #cell(group)="data">
                                    <span>{{ data.item.group ? data.item.group.name : '--' }}</span>
                                </template>
                                <template #cell(type)="data">
                                    <span>{{ data.item.propertyType ? data.item.propertyType.name : '--' }}</span>
                                </template>
                                <template #cell(isMalaysia)="data">
                                    <i :class="data.item.isMalaysia ? 'text-info mdi mdi-checkbox-marked-circle' : 'text-muted mdi mdi-close-circle'"></i>
                                </template>
                                <template #cell(isPersonal)="data">
                                    <i :class="data.item.isPersonal ? 'text-info mdi mdi-checkbox-marked-circle' : 'text-muted mdi mdi-close-circle'"></i>
                                </template>
                                <template #cell(status)="data">
                                    <b-badge
                                        :variant="tableRows[data.index].isActive ? 'success' : 'secondary'"
                                    >
                                        {{ tableRows[data.index].isActive ? $t('label.active') : $t('label.inactive') }}
                                    </b-badge>
                                </template>
                                <template #cell(actions)="data">
                                    <a
                                        href="javascript:void(0);"
                                        class="text-info"
                                        v-b-tooltip.hover
                                        :title="$t('label.updateX', [])"
                                        @click.stop="openForm(data.item)"
                                    >
                                        <i class="mdi mdi-pencil font-size-18"></i>
                                    </a>
                                    <a
                                        href="javascript:void(0);"
                                        class="text-info ml-1"
                                        v-b-tooltip.hover
                                        :title="data.item.isActive == 0 ? $t('label.restoreX', []) : $t('label.deleteX', [])"
                                        v-on:click="deleteConfirmation(data.item)"
                                    >
                                        <i :class="[`mdi font-size-18 mdi-delete-${data.item.isActive == 0 ? 'restore' : 'outline'}`]"></i>
                                    </a>
                                </template>
                            </b-table>
                            <div v-else class="d-flex justify-content-center align-items-center p-5 m-5">
                                <b-spinner class="m-5" variant="info" role="status"></b-spinner>
                            </div>
                            <b-alert v-if="tableRows.length == 0 && status.list && !loading"
                                show
                                variant="warning"
                                class="mt-0 text-center">
                                <i class="mdi mdi-alert-circle-outline"></i> {{ $t('message.noResultsFound') }}
                            </b-alert>
                        </div>
                        <div v-if="totalPage" class="d-flex flex-column flex-md-row justify-content-md-between align-items-center">
                            <small class="text-muted">{{ $t('label.total') }}: {{ totalPage }}</small>
                            <b-pagination
                                v-model="currentPage"
                                :total-rows="totalPage"
                                :per-page="perPage"
                                v-on:change="pageNavigation"
                                variant="info"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <b-modal
            id="property-tenanted-form"
            :title="$t(propertyTenanted.uuid ? 'label.updateX' : 'label.createX', [$t('label.tenanted')])"
            @hidden="closeForm" 
            hide-footer
            hide-header-close
            centered
            size="xl"
        >
            <b-container fluid>
                <PropertyTenantedForm
                    :propertyTenantedUUID="propertyTenanted.uuid"
                    :searchCriteria="searchCriteria"
                    @done="closeForm"
                    ref="propertyTenantedForm"
                />
            </b-container>
        </b-modal>
        <b-modal
            id="property-tenanted-delete"
            :title="propertyTenanted.isActive == 0 ? $t('label.restoreX', []) : $t('label.deleteX', [])"
            @hidden="closeForm"
            hide-header-close
        >
            <p class="my-4">
                {{ propertyTenanted.isActive == 0 ? $t('message.restoreConfirmation') : $t('message.deleteConfirmation') }}
            </p>
            <template #modal-footer>
                <b-button 
                    variant="danger" 
                    class="col-2" 
                    @click="$bvModal.hide('property-tenanted-delete')"
                >
                    {{ $t('label.cancel') }}
                </b-button>
                <b-button
                    variant="info"
                    class="ml-2 col-2"
                    v-on:click="requestDelete"
                    :disabled="!status.save"
                >
                    {{ propertyTenanted.isActive == 0 ? $t('label.restoreX', []) : $t('label.deleteX', []) }}
                </b-button>
            </template>
        </b-modal>
        <b-modal
            id="property-tenanted-image"
            :title="$t('label.viewX', [$t('label.image')])"
            @hidden="closeForm"
            centered
            size="lg"
            hide-header-close
        >
            <b-container fluid>
                <div class="col-12">
                    <div class="row d-flex justify-content-center border border-light shadow-lg m-1 p-3 rounded">
                        <img
                            :src="propertyTenanted.image"
                            style="max-width: 100%; max-height: 600px;"
                        />
                    </div>
                </div>
            </b-container>
            <template #modal-footer>
                <b-button
                    variant="danger"
                    class="col-2"
                    @click="$bvModal.hide('property-tenanted-image')"
                >
                    {{ $t('label.cancel') }}
                </b-button>
            </template>
        </b-modal>
    </div>
</template>
<script>
import Multiselect from "vue-multiselect";
import i18n from "@/i18n";
import { SESSION, SHARED } from "@/constants";
import { sessionHelper, ddlHelper } from "@/utils";
import { mapState } from "vuex";
import { propertyTenantedMethods, lookupMethods } from "@/state/api";
import { PROPERTY_TENANTED_MODULE } from "@/state/api/propertyTenanted.state";
import PropertyTenantedForm from "@/views/pages/admin/propertyTenanted/propertyTenantedForm";

export default {
    components: { Multiselect, PropertyTenantedForm },
    async created() {
        await this.lookupGroup({});
        await this.lookupPropertyType();

        sessionHelper.clearSearch(SESSION.PROPERTY_TENANTED_SEARCH) // clear search whenever page reload
        this.initSearchCriteria();

        this.loading = false;
    },
    data() {
        return {
            filter: "",
            currentPage: 1,
            perPage: SHARED.DEFAULT_PAGE_SIZE,
            statusDdl: ddlHelper.getActivationStatus(true),
            isActive: ddlHelper.getActivationStatus(true)[1],
            groupFilter: null,
            propertyTypeFilter: null,
            fields: [
                { key: 'landlord', label: "Landlord", sortable: true, class: 'text-nowrap' },
                { key: 'company', label: i18n.t("module.company"), sortable: true, class: 'text-nowrap' },
                { key: 'registeredOwner', label: 'Registered Owner', sortable: true, class: 'text-nowrap' },
                { key: 'group', label: i18n.t("module.group"), sortable: true, class: 'text-nowrap' },
                { key: 'type', label: i18n.t("label.type"), sortable: true, class: 'text-nowrap' },
                { key: 'isPersonal', label: 'Personal', sortable: false, class: 'text-nowrap' },
                { key: 'isMalaysia', label: 'Malaysia', sortable: false, class: 'text-nowrap' },
                { key: 'status', label: i18n.t("label.status"), sortable: true, class: 'text-nowrap' },
                { key: 'actions', label: i18n.t("label.action"), sortable: false, class: 'text-nowrap' },
            ],
            searchCriteria: {},
            propertyTenanted: {
                uuid: "",
                isActive: false,
                image: null,
            },
            loading: true,
        };
    },
    methods: {
        ...propertyTenantedMethods,
        ...lookupMethods,
        initSearchCriteria() {
            let isDefault = true;
            if (sessionHelper.hasSearchCriteria(SESSION.PROPERTY_TENANTED_SEARCH)) {
                isDefault = false;
                this.searchCriteria = sessionHelper.retrieveSearch(SESSION.PROPERTY_TENANTED_SEARCH);
                this.currentPage = this.searchCriteria.pageNumber;
            }
            if (isDefault) {
                this.searchCriteria = {
                    pageNumber: SHARED.DEFAULT_PAGE_NUMBER,
                    pageSize: SHARED.DEFAULT_PAGE_SIZE,
                    isTenanted: 1,
                    isActive: this.isActive.value,
                    group: null,
                    propertyType: null,
                    searchValue: "",
                    searchArray: [],
                };
            }
            this.getPropertyTenantedList(this.searchCriteria);
        },
        getPropertyTenantedList(filter) {
            this.getAllPropertyTenanted({ filter });
            this.searchCriteria = filter;
            sessionHelper.storeSearch(SESSION.PROPERTY_TENANTED_SEARCH, filter);
        },
        pageNavigation(pageNumber) {
            let filter = sessionHelper.retrieveSearch(SESSION.PROPERTY_TENANTED_SEARCH);
            filter.pageNumber = pageNumber;
            this.getPropertyTenantedList(filter);
        },
        searchListing() {
            this.searchCriteria.pageNumber = SHARED.DEFAULT_PAGE_NUMBER;
            this.searchCriteria.searchArray = [];
            this.searchCriteria.searchArray.push(this.filter);
            this.searchCriteria.isTenanted = 1;
            this.searchCriteria.isActive = this.isActive.value;
            if (this.filter == '' || this.filter == null) {
                this.searchCriteria.pageNumber = 1;
                this.currentPage = 1;
            }

            if (this.groupFilter?.value == 1)
                delete this.searchCriteria.group;
            else if (this.groupFilter?.value)
                this.searchCriteria.group = this.groupFilter.value;

            if (this.propertyTypeFilter?.value == 1)
                delete this.searchCriteria.propertyType;
            else if (this.propertyTypeFilter?.value)
                this.searchCriteria.propertyType = this.propertyTypeFilter.value;

            this.getPropertyTenantedList(this.searchCriteria);
        },
        deleteConfirmation(data) {
            this.propertyTenanted = {
                uuid: data.uuid,
                isActive: data.isActive,
            };
            this.$bvModal.show('property-tenanted-delete');
        },
        requestDelete() {
            let data = {
                uuid: this.propertyTenanted.uuid,
                isActive: this.propertyTenanted.isActive == 0 ? 1 : 0,
            };
            this.updatePropertyTenantedStatus({ data, filter: this.searchCriteria })
                .then((result) => this.$nextTick(() => {
                    if (result?.success) this.$bvModal.hide('property-tenanted-delete');
                }));
        },
        openForm(data) {
            if (data?.uuid) {
                this.propertyTenanted = {
                    uuid: data.uuid,
                    isActive: data.isActive,
                };
            }
            this.$nextTick(() => {
                this.$bvModal.show('property-tenanted-form');
            });
        },
        closeForm() {
            this.propertyTenanted = {
                uuid: "",
                isActive: false,
                image: null,
            };
            this.$bvModal.hide('property-tenanted-form');
        },
        openImageModal(data) {
            if (data?.imageUrl) {
                this.propertyTenanted = {
                    uuid: data.uuid,
                    image: data.imageUrl,
                };
                this.$bvModal.show('property-tenanted-image');
            }  
        },
        avatarColor(item) {
            const colors = ['primary', 'success', 'danger', 'warning', 'info'];
            if (!item) return 'secondary';
            return colors[item.length % colors.length];
        },
    },
    computed: {
        ...mapState(PROPERTY_TENANTED_MODULE, ['status']),
        groupDdl() {
            return [
                { value: 1, text: i18n.t('label.all') },
                ...this.$store.state.api.lookup.groupDdl,
            ];
        },
        propertyTypeDdl() {
            return [
                { value: 1, text: i18n.t('label.all') },
                ...this.$store.state.api.lookup.propertyTypeDdl,
            ];
        },
        tableRows() {
            return this.$store.state.api.propertyTenanted.list.data || [];
        },
        totalPage() {
            return this.$store.state.api.propertyTenanted.list.pagination.total || 0;
        },
    },
}
</script>
